import { ErrorHandler, Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import * as Sentry from '@sentry/angular';

/**
 * After a deploy, the hashed lazy chunks change name. A tab still running the
 * previous build requests an old chunk that no longer exists; the server answers
 * with index.html (text/html) or a 404, so the dynamic import / `@defer` load
 * fails (NG0750, ChunkLoadError, "Failed to load module script…"). The app is now
 * stuck on a dead version.
 *
 * Mitigation: detect that specific failure and do a single hard reload to pull the
 * fresh index.html (which references the new chunk names). A sessionStorage cooldown
 * prevents reload loops if the error is actually persistent (so we never trap the
 * user in a refresh cycle). Every error still goes through Sentry's handler; the
 * `ignoreErrors` list in main.ts drops the chunk-load ones so a deploy doesn't burn
 * the quota.
 */
const CHUNK_ERROR_PATTERNS = [
  'ChunkLoadError',
  'Loading chunk',
  'error loading dynamically imported module',
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'Expected a JavaScript-or-Wasm module script', // strict MIME failure
  'NG0750', // Angular: @defer block dependencies failed to load
];

const RELOAD_AT_KEY = 'chunkReloadAt';
const RELOAD_COUNT_KEY = 'chunkReloadCount';
const RELOAD_COOLDOWN_MS = 20_000; // avoid rapid re-fire within a single error burst
const MAX_RELOADS_PER_SESSION = 2; // hard stop: a genuinely broken build can never loop

@Injectable()
export class ChunkReloadErrorHandler implements ErrorHandler {
  // Preserve Sentry's reporting — we wrap it, we don't replace it.
  #sentryHandler = Sentry.createErrorHandler();

  constructor(@Inject(PLATFORM_ID) private platformId: object) {
    if (isPlatformBrowser(this.platformId)) {
      // Dynamic-import rejections can surface as unhandled promise rejections
      // instead of going through Angular's ErrorHandler — cover that path too.
      window.addEventListener('unhandledrejection', (event) => {
        if (this.#isChunkLoadError(event.reason)) this.#tryReload();
      });
    }
  }

  handleError(error: any): void {
    if (this.#isChunkLoadError(error)) {
      // Hand it to Sentry (main.ts `ignoreErrors` drops it) before we navigate
      // away, then reload.
      try { this.#sentryHandler.handleError(error); } catch { /* ignore */ }
      this.#tryReload();
      return;
    }
    this.#sentryHandler.handleError(error);
  }

  #isChunkLoadError(error: any): boolean {
    if (!error) return false;
    const haystack = [error.name, error.message, error.toString?.()]
      .filter(Boolean)
      .join(' ');
    return CHUNK_ERROR_PATTERNS.some((pattern) => haystack.includes(pattern));
  }

  #tryReload(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      // Hard cap per tab session: a genuinely broken build (not a transient stale
      // chunk) must never trap the user in a refresh cycle. The counter survives
      // reloads via sessionStorage, so after N attempts we stop and let the error
      // surface normally.
      const count = Number(sessionStorage.getItem(RELOAD_COUNT_KEY) ?? 0);
      if (count >= MAX_RELOADS_PER_SESSION) return;

      const last = Number(sessionStorage.getItem(RELOAD_AT_KEY) ?? 0);
      const now = Date.now();
      if (now - last < RELOAD_COOLDOWN_MS) return; // coalesce a burst of failing chunks into one reload

      sessionStorage.setItem(RELOAD_COUNT_KEY, String(count + 1));
      sessionStorage.setItem(RELOAD_AT_KEY, String(now));
      window.location.reload();
    } catch {
      /* sessionStorage/location unavailable → give up silently */
    }
  }
}
